import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DiscoveryTraceEvent, DiscoveryTurnTrace } from '@discovr/types';
import { discoveryApi } from '../api/discoveryApi';
import { PERSONA_TRACE_POLL_MS } from '../constants';
import { discoveryKeys } from '../queryKeys';

export function useVettingCatalog(campaignId: string) {
  return useQuery({
    queryKey: discoveryKeys.vettingCatalog(campaignId),
    queryFn: () => discoveryApi.vettingCatalog(campaignId),
    enabled: campaignId.length > 0,
  });
}

const NO_STARTERS: readonly string[] = [];

export function usePersonaStarters(
  campaignId: string,
  castVersion: string,
  enabled: boolean,
): readonly string[] {
  const { data } = useQuery({
    queryKey: discoveryKeys.personaStarters(campaignId, castVersion),
    queryFn: () => discoveryApi.personaStarters(campaignId),
    select: (written) => written.starters,
    enabled: enabled && campaignId.length > 0,
  });
  return data ?? NO_STARTERS;
}

export function useChatThreads(campaignId: string, personaId: string | null) {
  return useQuery({
    queryKey: discoveryKeys.personaChats(campaignId, personaId),
    queryFn: () => discoveryApi.chatThreads(campaignId, personaId),
    enabled: campaignId.length > 0,
  });
}

export interface TurnTrace {
  next: number;
  events: DiscoveryTraceEvent[];
}

const EMPTY_TRACE: TurnTrace = { next: 0, events: [] };

function mergeTrace(before: TurnTrace, page: DiscoveryTurnTrace): TurnTrace {
  const seen = new Set(before.events.map((event) => event.id));
  const fresh = page.events.filter((event) => !seen.has(event.id));
  return fresh.length
    ? { next: page.next, events: [...before.events, ...fresh] }
    : { ...before, next: Math.max(before.next, page.next) };
}

export function useTurnTrace(campaignId: string, turnId: string | null, live: boolean) {
  const queryClient = useQueryClient();
  const queryKey = discoveryKeys.turnTrace(campaignId, turnId);
  return useQuery({
    queryKey,
    queryFn:
      campaignId && turnId
        ? async () => {
            const before = queryClient.getQueryData<TurnTrace>(queryKey) ?? EMPTY_TRACE;
            return mergeTrace(
              before,
              await discoveryApi.turnEvents(campaignId, turnId, before.next),
            );
          }
        : skipToken,
    refetchInterval: live ? PERSONA_TRACE_POLL_MS : false,
  });
}
